import { useContext, useRef, type ReactNode } from 'react';
import { Link, useInRouterContext, useLocation } from 'react-router-dom';
import { Droplets, Fish, LayoutGrid, Menu, Mic, Plus } from 'lucide-react';
import { Button } from '../../atoms/Button/Button';
import { Badge } from '../../atoms/Badge/Badge';
import { useTranslation } from '../../../i18n/useTranslation';
import { ActiveAquariumContext } from '../../../contexts/AquariumContext';
import { navigationConfig } from '../../../config/navigation';
import { aquariumIdFromPath, findActiveArea } from '../../../config/navigationMatch';
import { useOptionalCapture } from '../../capture/CaptureContext';
import './MobileTabBar.css';

export interface MobileTabBarProps {
  /** Opens the full navigation (the sidebar drawer). */
  onMore: () => void;
  /** Id of the drawer the "Mehr" button controls. */
  drawerId: string;
  drawerOpen: boolean;
}

/** Holding the Capture tab this long starts recording directly (a shortcut only). */
export const LONG_PRESS_MS = 500;

/**
 * Phones only (≤ 480 px): the four places used most, one thumb away, and "Mehr"
 * for the full navigation (usability audit §4.1 / §6). The centre button is
 * **Capture** (voice journal, usability review H1): it opens the capture sheet
 * (Record note · Log tests · Photo) and shows how many notes wait in To review;
 * a long press records directly. Where voice notes are unavailable it stays the
 * "Log tests" link. Without an aquarium only Heute and Mehr remain.
 */
export function MobileTabBar(props: MobileTabBarProps) {
  return useInRouterContext() ? <RoutedMobileTabBar {...props} /> : null;
}

function RoutedMobileTabBar({ onMore, drawerId, drawerOpen }: MobileTabBarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = useContext(ActiveAquariumContext);
  const aquariumId = aquariumIdFromPath(pathname) ?? active?.activeAquariumId ?? null;
  const currentArea = findActiveArea(navigationConfig, pathname)?.item.labelKey ?? null;
  const aq = aquariumId === null ? null : `/aquariums/${encodeURIComponent(aquariumId)}`;
  const onSession = pathname.endsWith('/measurements/session');
  const capture = useOptionalCapture();

  const link = (labelKey: string, to: string, icon: ReactNode, current: boolean) => (
    <li className="rg-mobile-tab-bar__item">
      <Link to={to} className="rg-mobile-tab-bar__link" aria-current={current ? 'page' : undefined}>
        {icon}
        <span className="rg-mobile-tab-bar__label">{t(labelKey)}</span>
      </Link>
    </li>
  );

  return (
    <nav className="rg-mobile-tab-bar" aria-label={t('nav.mobileTabBar')} data-testid="mobile-tab-bar">
      <ul className="rg-mobile-tab-bar__list">
        {link('navItem.today', '/dashboard', <LayoutGrid size={20} aria-hidden="true" />, currentArea === 'navItem.today')}
        {aq !== null && link('navItem.water', `${aq}/measurements`, <Droplets size={20} aria-hidden="true" />, currentArea === 'navItem.water' && !onSession)}
        {aq !== null && (capture?.available ? (
          <li className="rg-mobile-tab-bar__item">
            <CaptureTab />
          </li>
        ) : (
          <li className="rg-mobile-tab-bar__item">
            <Link
              to={`${aq}/measurements/session`}
              className="rg-mobile-tab-bar__link rg-mobile-tab-bar__link--primary"
              aria-current={onSession ? 'page' : undefined}
            >
              <Plus size={20} aria-hidden="true" />
              {/* Short label: "Tests erfassen" did not fit a fifth of a phone screen. */}
              <span className="rg-mobile-tab-bar__label">{t('nav.logTestsShort')}</span>
            </Link>
          </li>
        ))}
        {/* "Tiere & Gesundheit" is too long for a fifth of a phone screen; the tab's own label fits. */}
        {aq !== null && link('navItem.animals', `${aq}/livestock`, <Fish size={20} aria-hidden="true" />, currentArea === 'navItem.animalsHealth')}
        <li className="rg-mobile-tab-bar__item">
          <Button
            variant="ghost"
            className="rg-mobile-tab-bar__link rg-mobile-tab-bar__more"
            aria-controls={drawerId}
            aria-expanded={drawerOpen}
            onClick={onMore}
          >
            <Menu size={20} aria-hidden="true" />
            <span className="rg-mobile-tab-bar__label">{t('navItem.more')}</span>
          </Button>
        </li>
      </ul>
    </nav>
  );
}

/** The centre "Capture" button: tap → capture sheet, long press → record now. */
function CaptureTab() {
  const { t } = useTranslation();
  const capture = useOptionalCapture();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);
  if (!capture) return null;
  const count = capture.toReview;

  const cancel = (): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <Button
      variant="ghost"
      className="rg-mobile-tab-bar__link rg-mobile-tab-bar__link--primary rg-mobile-tab-bar__capture"
      aria-label={count > 0 ? String(t('capture.tabWithCount', { count })) : undefined}
      aria-haspopup="dialog"
      data-testid="mobile-tab-capture"
      onPointerDown={() => {
        longPressed.current = false;
        cancel();
        timer.current = setTimeout(() => {
          longPressed.current = true;
          capture.startRecording();
        }, LONG_PRESS_MS);
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={(event) => event.preventDefault()}
      onClick={() => {
        cancel();
        if (longPressed.current) {
          longPressed.current = false;
          return;
        }
        capture.openCaptureSheet();
      }}
    >
      <span className="rg-mobile-tab-bar__icon">
        <Mic size={20} aria-hidden="true" />
        {count > 0 && (
          <Badge tone="info" className="rg-mobile-tab-bar__count" aria-hidden="true">
            {count > 99 ? '99+' : count}
          </Badge>
        )}
      </span>
      <span className="rg-mobile-tab-bar__label">{t('capture.tab')}</span>
    </Button>
  );
}
